import { z } from 'zod';
import {
  bigIntToNumber,
  bigIntToNumberNullable,
  priceValidatorNullable,
  dateValidator,
  nonNegativeIntValidatorNullable,
} from '../common/validators';

/**
 * Transaction status enum
 * Maps string status codes to numeric values
 * Status codes: 0-9 representing different transaction states
 */
export const TransactionStatusEnum = z
  .enum(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'])
  .transform((val) => parseInt(val, 10));

export type TransactionStatus = z.infer<typeof TransactionStatusEnum>;

/**
 * What Sellwild took on the sale, as recorded on the row.
 *
 * Shared so the seller, buyer and single-transaction payloads describe the fee
 * identically — they used to omit it entirely, which is why every surface
 * recomputed it from the flat 10% constant and showed the wrong number to any
 * seller holding a fee grant.
 *
 * `commissionAmount` is nullable for rows created before the columns existed.
 * Null means "not recorded", not "no fee" — see utils/transaction-commission.ts.
 */
export const CommissionFieldsShape = {
  /** Contractual rate as a decimal (0.05), before the $500 cap */
  commissionRate: z.number().min(0).max(1),
  /** Dollars taken, cap and rounding applied. Null on legacy rows. */
  commissionAmount: z.number().min(0).nullable(),
};

/**
 * Base transaction schema with core fields
 * Contains essential transaction information
 */
export const BaseTransactionSchema = z.object({
  /** Transaction ID */
  id: z.number().int().positive(),
  /** Seller user ID */
  sellerId: bigIntToNumberNullable,
  /** Buyer user ID */
  buyerId: bigIntToNumberNullable,
  /** Associated listing ID */
  listingId: z.number().int().positive(),
  /** Transaction status (0-9) */
  status: z.number().int().min(0).max(9).nullable(),
  /** Whether seller has read the transaction */
  read: z.boolean().nullable(),
  /** Whether buyer has read the transaction */
  buyerRead: z.boolean().nullable(),
  /** Transaction price (string decimal converted to number) */
  price: priceValidatorNullable,
  /** Transaction creation timestamp */
  createdDate: dateValidator,
  /** Last modification timestamp */
  lastModified: dateValidator,
});

export type BaseTransaction = z.infer<typeof BaseTransactionSchema>;

/**
 * Full transaction schema with all fields
 * Extends base schema with payment and shipping information
 */
export const TransactionSchema = BaseTransactionSchema.extend({
  /** Correlation ID for transaction tracking */
  correlationId: z.string().max(255).nullable(),
  /** Associated payment ID */
  paymentId: nonNegativeIntValidatorNullable,
  /** Associated shipping ID */
  shippingId: nonNegativeIntValidatorNullable,
  /** Associated commission payment ID */
  commissionId: nonNegativeIntValidatorNullable,
  ...CommissionFieldsShape,
  /** Debug ID for troubleshooting */
  debugId: z.string().max(255).nullable(),
});

export type Transaction = z.infer<typeof TransactionSchema>;
